import { useGlobalModal } from '@/hooks/useGlobalModal';
import DeviceModalTitle from '@components/DeviceModalTitle';
import GlobalModal from '@components/GlobalModal';
import ModalTabs, { Panel } from '@components/ModalTabs';
import { Access } from '@features/authorization';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { useMemo, type FC } from 'react';
import CalibrationTab from './tabs/calibrationTab';
import FiltersTab from './tabs/filtersTab';
import FlowLimitsTab from './tabs/flowLimitsTab';
import MathModelingTab from './tabs/mathModelingTab';
import RadarParametersTab from './tabs/radarTab';
import RatingCurvesTab from './tabs/ratingCurvesTab';
import { type DeviceTabProps } from './tabs/types';

const DeviceDataConfigurationModal: FC = () => {
  const { globalModalProps, onClose, state, setIsDirty, changeTabMiddleware } = useGlobalModal(
    'updateDeviceDataConfigurationModal',
    {
      closeConfirmation: true,
    },
  );

  const tabs = useTabs(state.open ? state.deviceId : null, onClose, setIsDirty);

  return (
    <GlobalModal
      {...globalModalProps}
      dialogProps={{
        maxWidth: 'xl',
      }}
      authorization={{
        access: Access.DeviceDataConfigurationModal,
        deviceId: state.open ? state.deviceId : undefined,
        onClose,
      }}
    >
      {state.open && (
        <>
          <DeviceModalTitle onClose={onClose} deviceId={state.deviceId} />
          <ModalTabs
            changeTabMiddleware={changeTabMiddleware}
            deviceId={state.deviceId}
            panels={tabs}
          />
        </>
      )}
    </GlobalModal>
  );
};

const useTabs = (
  deviceId: string | null,
  onClose: () => void,
  setIsDirty: React.Dispatch<React.SetStateAction<boolean>>,
): Array<Panel> => {
  const { i18n } = useLingui();

  return useMemo(() => {
    if (!deviceId) return [];
    const props: DeviceTabProps = {
      deviceId,
      onClose,
      setIsDirty,
    };
    return [
      {
        key: 'calibration',
        label: t(i18n)`General`,
        component: <CalibrationTab {...props} />,
        access: Access.DeviceDataConfiguration_CalibrationTab,
      },
      {
        key: 'flow-limits',
        label: t(i18n)`Límites de Caudal`,
        component: <FlowLimitsTab {...props} />,
        access: Access.DeviceDataConfiguration_FlowLimitTab,
      },
      {
        key: 'filters',
        label: t(i18n)`Filtros`,
        component: <FiltersTab {...props} />,
        access: Access.DeviceDataConfiguration_FiltersTab,
      },
      {
        key: 'rating-curves',
        label: t(i18n)`Curvas de Aforo`,
        component: <RatingCurvesTab {...props} />,
        access: Access.DeviceDataConfiguration_RatingCurvesTab,
      },
      {
        key: 'radar',
        label: t(i18n)`Parámetros Radar`,
        component: <RadarParametersTab {...props} />,
        access: Access.DeviceDataConfiguration_RadarTab,
      },
      {
        key: 'math-modeling',
        label: t(i18n)`Modelamientos Matemáticos`,
        component: <MathModelingTab {...props} />,
        access: Access.DeviceDataConfiguration_MathModelingTab,
      },
    ];
  }, [deviceId, i18n, onClose, setIsDirty]);
};

export default DeviceDataConfigurationModal;
